import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TreeSelect, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const { SHOW_PARENT } = TreeSelect;
const { confirm } = Modal;

const Treeselect = ({
    id,
    options,
    value,
    onChange,
    name,
    disabled = false,
    clearable = true,
    searchable = true,
    placeholder = 'Select...',
    multiSelect = false,
    sortable = false,
    callback = () => { },
    className = '',
    rtl = false,
    alwaysOpen = false,
    confirmClear = false,
    dropdownWidth = '100px',
    showStrategy = 'SHOW_PARENT'
}) => {

    const convertOptions = useCallback((items) => {
        return items.map(item => {
            const identifier = item.value !== undefined ?
                item.value :
                `key-${item.name || item.title || Math.random().toString(36).substr(2, 9)}`;

            return {
                title: item.name || item.label || item.title,
                value: identifier,
                key: identifier,
                disabled: item.disabled,
                children: item.children ? convertOptions(item.children) : [],
            };
        });
    }, []);

    const treeData = useMemo(() => {
        const converted = convertOptions(options || []);
        if (sortable) {
            converted.sort((a, b) => (a.title > b.title ? 1 : -1));
        }
        return converted;
    }, [options, sortable, convertOptions]);

    const handleChange = useCallback((newValue, labelList, extra) => {
        if (confirmClear && value && (!newValue || (Array.isArray(newValue) && newValue.length === 0))) {
            confirm({
                title: "Are you sure?",
                icon: <ExclamationCircleOutlined className="text-primary" />,
                content: "You will not be able to undo this action",
                onOk() {
                    if (onChange) {
                        onChange(newValue, extra);
                    }
                    if (callback) {
                        callback({ detail: newValue });
                    }
                },
                onCancel() {
                    console.log("Clear canceled");
                },
            });
        } else {
            if (onChange) {
                onChange(newValue, extra);
            }
            if (callback) {
                callback({ detail: newValue });
            }
        }

    }, [confirmClear, value, onChange, callback]);

    const filterTreeNode = useCallback(
        searchable
            ? (input, treeNode) => {
                return String(treeNode.title).toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }
            : false,
        [searchable]
    );

    const showStrategies = {
        SHOW_ALL: TreeSelect.SHOW_ALL,
        SHOW_PARENT: TreeSelect.SHOW_PARENT,
        SHOW_CHILD: TreeSelect.SHOW_CHILD,
    };

    return (
        <div
            id={id}
            className={`treeselect-container ${className}`}
            name={name}
        >
            <TreeSelect
                style={{ width: '100%' }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto', minWidth: dropdownWidth }}
                treeData={treeData}
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                allowClear={clearable}
                showSearch={searchable}
                multiple={multiSelect}
                treeCheckable={multiSelect}
                showCheckedStrategy={showStrategies[showStrategy] || TreeSelect.SHOW_PARENT}
                disabled={disabled}
                treeDefaultExpandAll={treeData.length < 50}
                filterTreeNode={filterTreeNode}
                open={alwaysOpen ? true : undefined}
                virtual={true}
                listHeight={300}
                listItemHeight={20}
                direction={rtl ? 'rtl' : 'ltr'}
                dropdownClassName={rtl ? 'rtl-dropdown' : ''}
                treeLine
            />
        </div>
    );
};

Treeselect.propTypes = {
    id: PropTypes.string,
    options: PropTypes.array.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array
    ]),
    onChange: PropTypes.func,
    name: PropTypes.string,
    disabled: PropTypes.bool,
    clearable: PropTypes.bool,
    searchable: PropTypes.bool,
    placeholder: PropTypes.string,
    showCount: PropTypes.bool,
    multiSelect: PropTypes.bool,
    flattenSearchResults: PropTypes.bool,
    sortable: PropTypes.bool,
    callback: PropTypes.func,
    className: PropTypes.string,
    rtl: PropTypes.bool,
    alwaysOpen: PropTypes.bool,
    listSlotHtmlComponent: PropTypes.object,
    confirmClear: PropTypes.bool,
    showStrategy: PropTypes.oneOf(['SHOW_ALL', 'SHOW_PARENT', 'SHOW_CHILD']),

};

export default React.memo(Treeselect);
